<template>
  <v-main>
    <v-container fluid>
      <!-- Clean Header Bar -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="header-bar">
            <div class="header-left">
              <h2 class="title">Redis Monitor</h2>
              <div class="status-indicator" :class="{ active: isConnected }"></div>
            </div>
            
            <div class="header-center" v-if="redisInfo">
              <div class="metric">
                <span class="value">{{ redisInfo.connected_clients }}</span>
                <span class="label">clients</span>
              </div>
              <div class="metric">
                <span class="value">{{ redisInfo.used_memory_human }}</span>
                <span class="label">memory</span>
              </div>
              <div class="metric">
                <span class="value">{{ calculateHitRate(redisInfo.keyspace_hits, redisInfo.keyspace_misses) }}%</span>
                <span class="label">hit rate</span>
              </div>
              <div class="metric">
                <span class="value">{{ formatUptime(redisInfo.uptime_in_seconds) }}</span>
                <span class="label">uptime</span>
              </div>
            </div>
            
            <div class="header-right">
              <label class="auto-refresh-toggle">
                <input 
                  type="checkbox" 
                  v-model="autoRefresh" 
                  @change="toggleAutoRefresh"
                >
                <span class="toggle-text">Auto</span>
              </label>
              <button 
                class="refresh-btn" 
                @click="refreshAll"
                :disabled="isRefreshing"
              >
                <v-icon small :class="{ spinning: isRefreshing }">mdi-refresh</v-icon>
              </button>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Queue List and Failed Jobs Row -->
      <v-row>
        <!-- Queue List Section -->
        <v-col cols="12" md="6">
          <v-card elevation="6" class="fill-height">
            <v-card-title>
              <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
              Queue List
            </v-card-title>
            <v-card-text>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Queue Name</th>
                      <th class="text-left">Jobs Count</th>
                      <th class="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="queue in queues" :key="queue.name">
                      <td>{{ queue.name }}</td>
                      <td>
                        <v-chip 
                          :color="queue.jobs_count > 0 ? 'warning' : 'success'" 
                          small
                        >
                          {{ queue.jobs_count }}
                        </v-chip>
                      </td>
                      <td class="text-right">
                        <v-btn
                          color="#6c5dd4"
                          x-small
                          @click="viewQueueJobs(queue.name)"
                          :disabled="queue.jobs_count === 0"
                        >
                          View Jobs
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Failed Jobs Section -->
        <v-col cols="12" md="6">
          <v-card elevation="6" class="fill-height">
            <v-card-title>
              <v-icon class="mr-2">mdi-alert-circle</v-icon>
              Failed Jobs
            </v-card-title>
            <v-card-text>
              <v-simple-table v-if="failedJobs.length > 0">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Job ID</th>
                      <th class="text-left">Job Class</th>
                      <th class="text-left">Failed At</th>
                      <th class="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="job in failedJobs" :key="job.id">
                      <td>{{ job.id }}</td>
                      <td>{{ job.job }}</td>
                      <td>{{ job.failed_at }}</td>
                      <td class="text-right">
                        <v-btn
                          color="error"
                          x-small
                          @click="viewJobDetails(job)"
                        >
                          View Error
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-alert v-else type="info" dense>
                No failed jobs found
                <v-btn
                  color="primary"
                  x-small
                  class="ml-2"
                  @click="debugRedis"
                >
                  Debug Redis
                </v-btn>
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Redis Keys Section -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6">
            <v-card-title>
              <v-icon class="mr-2">mdi-key</v-icon>
              Redis Keys
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="keyPattern"
                    label="Key Pattern"
                    placeholder="e.g., cache:*, queues:*"
                    dense
                    outlined
                    @keyup.enter="searchKeys"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="keyLimit"
                    label="Limit"
                    type="number"
                    dense
                    outlined
                    min="1"
                    max="1000"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-btn
                    color="#6c5dd4"
                    @click="searchKeys"
                    :loading="isSearchingKeys"
                  >
                    Search Keys
                  </v-btn>
                </v-col>
              </v-row>
              
              <v-simple-table v-if="redisKeys.length > 0">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Key</th>
                      <th class="text-left">Type</th>
                      <th class="text-left">TTL</th>
                      <th class="text-left">Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="key in redisKeys" :key="key.key">
                      <td style="font-family: monospace;">{{ key.key }}</td>
                      <td>
                        <v-chip x-small :color="getTypeColor(key.type)">
                          {{ key.type }}
                        </v-chip>
                      </td>
                      <td>{{ formatTTL(key.ttl) }}</td>
                      <td>{{ key.size }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              
              <v-alert v-else-if="hasSearchedKeys" type="info" dense>
                No keys found matching pattern "{{ keyPattern }}"
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Recent Logs Section (Bottom) -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6">
            <v-card-title>
              <v-icon class="mr-2">mdi-text-box</v-icon>
              Recent Logs
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                x-small
                @click="debugLogs"
              >
                Debug Logs
              </v-btn>
            </v-card-title>
            <v-card-text>
              <!-- Search Controls -->
              <v-row class="mb-3" v-if="recentLogs.length > 0">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="logSearchText"
                    label="Search logs"
                    placeholder="Search by message, timestamp, or any text..."
                    dense
                    outlined
                    clearable
                    prepend-inner-icon="mdi-magnify"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="logLevelFilter"
                    :items="logLevels"
                    label="Filter by level"
                    dense
                    outlined
                    clearable
                    multiple
                    chips
                    small-chips
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3" class="d-flex align-center">
                  <v-chip
                    v-if="filteredLogs.length !== recentLogs.length"
                    color="primary"
                    small
                    class="mr-2"
                  >
                    {{ filteredLogs.length }} / {{ recentLogs.length }}
                  </v-chip>
                  <v-btn
                    color="grey"
                    x-small
                    text
                    @click="clearLogSearch"
                    v-if="logSearchText || logLevelFilter.length > 0"
                  >
                    Clear Filters
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Logs Display -->
              <div class="log-container" v-if="filteredLogs.length > 0">
                <div 
                  v-for="(log, index) in filteredLogs" 
                  :key="index"
                  class="log-entry"
                  :class="getLogLevelClass(log.level)"
                >
                  <span class="log-timestamp">{{ log.timestamp }}</span>
                  <span class="log-level">{{ log.level }}</span>
                  <span class="log-message" v-html="highlightSearchText(log.message)"></span>
                </div>
              </div>
              <v-alert v-else-if="recentLogs.length > 0 && filteredLogs.length === 0" type="warning" dense>
                No logs match your search criteria
              </v-alert>
              <v-alert v-else type="info" dense>
                No recent logs found
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Job Details Dialog -->
    <v-dialog v-model="jobDetailsDialog" max-width="800">
      <v-card>
        <v-card-title>
          Job Details
          <v-spacer></v-spacer>
          <v-btn icon @click="jobDetailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="selectedJob">
          <v-row>
            <v-col cols="12">
              <strong>Job ID:</strong> {{ selectedJob.id }}<br>
              <strong>Job Class:</strong> {{ selectedJob.job }}<br>
              <strong>Failed At:</strong> {{ selectedJob.failed_at }}
            </v-col>
            <v-col cols="12" v-if="selectedJob.exception">
              <strong>Exception:</strong>
              <pre class="error-text">{{ selectedJob.exception }}</pre>
            </v-col>
            <v-col cols="12" v-if="selectedJob.data">
              <strong>Job Data:</strong>
              <pre>{{ JSON.stringify(selectedJob.data, null, 2) }}</pre>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Queue Jobs Dialog -->
    <v-dialog v-model="queueJobsDialog" max-width="1000">
      <v-card>
        <v-card-title>
          Queue Jobs: {{ selectedQueueName }}
          <v-spacer></v-spacer>
          <v-btn icon @click="queueJobsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-simple-table v-if="queueJobs.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Job ID</th>
                  <th class="text-left">Job Class</th>
                  <th class="text-left">Attempts</th>
                  <th class="text-left">Created At</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="job in queueJobs" :key="job.id">
                  <td>{{ job.id }}</td>
                  <td>{{ job.job }}</td>
                  <td>{{ job.attempts }}</td>
                  <td>{{ job.created_at }}</td>
                  <td class="text-right">
                    <v-btn
                      color="#6c5dd4"
                      x-small
                      class="mr-2"
                      @click="runJob(job)"
                      :loading="job.isRunning"
                      :disabled="job.isRunning || job.isRemoving"
                    >
                      <v-icon x-small class="mr-1">mdi-play</v-icon>
                      Run
                    </v-btn>
                    <v-btn
                      color="error"
                      x-small
                      @click="removeJob(job)"
                      :loading="job.isRemoving"
                      :disabled="job.isRemoving || job.isRunning"
                    >
                      <v-icon x-small class="mr-1">mdi-delete</v-icon>
                      Remove
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-alert v-else type="info" dense>
            No jobs found in this queue
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "RedisMonitorPage",
  data() {
    return {
      isLoading: false,
      isRefreshing: false,
      isSearchingKeys: false,
      isConnected: false,
      autoRefresh: false,
      refreshInterval: null,
      
      // Redis Info
      redisInfo: null,
      
      // Queues
      queues: [],
      
      // Failed Jobs
      failedJobs: [],
      
      // Logs
      recentLogs: [],
      logSearchText: "",
      logLevelFilter: [],
      
      // Redis Keys
      redisKeys: [],
      keyPattern: "*",
      keyLimit: 100,
      hasSearchedKeys: false,
      
      // Dialogs
      jobDetailsDialog: false,
      selectedJob: null,
      queueJobsDialog: false,
      selectedQueueName: "",
      queueJobs: [],
    };
  },
  computed: {
    // Get unique log levels from recent logs for filter dropdown
    logLevels() {
      const levels = [...new Set(this.recentLogs.map(log => log.level))];
      return levels.sort();
    },

    // Filter logs based on search text and level filter
    filteredLogs() {
      let filtered = this.recentLogs;

      // Filter by search text
      if (this.logSearchText) {
        const searchText = this.logSearchText.toLowerCase();
        filtered = filtered.filter(log => {
          return (
            log.message.toLowerCase().includes(searchText) ||
            log.timestamp.toLowerCase().includes(searchText) ||
            log.level.toLowerCase().includes(searchText) ||
            log.full_line.toLowerCase().includes(searchText)
          );
        });
      }

      // Filter by log level
      if (this.logLevelFilter.length > 0) {
        filtered = filtered.filter(log => this.logLevelFilter.includes(log.level));
      }

      return filtered;
    }
  },
  async mounted() {
    await this.refreshAll();
    // Auto-search Redis keys on load
    await this.searchKeys();
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    async refreshAll() {
      this.isRefreshing = true;
      await Promise.all([
        this.getRedisInfo(),
        this.getQueues(),
        this.getFailedJobs(),
        this.getRecentLogs(),
      ]);
      this.isRefreshing = false;
    },

    async getRedisInfo() {
      try {
        const response = await axios.get("/redis-monitor/info");
        this.redisInfo = response.data.data;
        this.isConnected = response.data.success;
      } catch (e) {
        this.isConnected = false;
        console.error("Failed to get Redis info:", e);
      }
    },

    async getQueues() {
      try {
        const response = await axios.get("/redis-monitor/queues");
        this.queues = response.data.data;
      } catch (e) {
        console.error("Failed to get queues:", e);
      }
    },

    async getFailedJobs() {
      try {
        const response = await axios.get("/redis-monitor/failed-jobs");
        this.failedJobs = response.data.data;
      } catch (e) {
        console.error("Failed to get failed jobs:", e);
      }
    },

    async getRecentLogs() {
      try {
        const response = await axios.get("/redis-monitor/logs");
        this.recentLogs = response.data.data;
      } catch (e) {
        console.error("Failed to get logs:", e);
      }
    },

    async searchKeys() {
      this.isSearchingKeys = true;
      try {
        const response = await axios.get("/redis-monitor/keys", {
          params: {
            pattern: this.keyPattern,
            limit: this.keyLimit,
          },
        });
        this.redisKeys = response.data.data;
        this.hasSearchedKeys = true;
      } catch (e) {
        console.error("Failed to search keys:", e);
      }
      this.isSearchingKeys = false;
    },

    async viewQueueJobs(queueName) {
      try {
        const response = await axios.get(`/redis-monitor/queues/${queueName}/jobs`);
        // Add loading states to each job
        this.queueJobs = response.data.data.map(job => ({
          ...job,
          isRunning: false,
          isRemoving: false
        }));
        this.selectedQueueName = queueName;
        this.queueJobsDialog = true;
      } catch (e) {
        console.error("Failed to get queue jobs:", e);
      }
    },

    async runJob(job) {
      if (job.isRunning || job.isRemoving) return;
      
      try {
        // Set loading state
        this.$set(job, 'isRunning', true);
        
        const response = await axios.post('/redis-monitor/run-job', {
          queue: this.selectedQueueName,
          job_index: job.queue_index
        });
        
        this.$vs.notification({
          title: "Job Started",
          text: response.data.message || "Job executed successfully",
          color: "success",
        });
        
        // Refresh the queue jobs list after a short delay
        setTimeout(async () => {
          await this.viewQueueJobs(this.selectedQueueName);
          // Also refresh the main queue list to update job counts
          await this.getQueues();
        }, 1000);
        
      } catch (e) {
        console.error("Failed to run job:", e);
        this.$vs.notification({
          title: "Job Run Failed",
          text: e.response?.data?.message || "Failed to run job. Check Failed Jobs section for details.",
          color: "danger",
        });
        
        // Refresh failed jobs and queue list when job fails
        setTimeout(async () => {
          await this.viewQueueJobs(this.selectedQueueName);
          await this.getQueues();
          await this.getFailedJobs(); // Refresh failed jobs to show the new failure
        }, 1000);
        
      } finally {
        // Reset loading state
        this.$set(job, 'isRunning', false);
      }
    },

    async removeJob(job) {
      if (job.isRemoving || job.isRunning) return;
      
      // Confirm before removing
      const confirmed = confirm(`Are you sure you want to remove this job?\n\nJob: ${job.job}\nID: ${job.id}`);
      if (!confirmed) return;
      
      try {
        // Set loading state
        this.$set(job, 'isRemoving', true);
        
        const response = await axios.delete('/redis-monitor/remove-job', {
          data: {
            queue: this.selectedQueueName,
            job_index: job.queue_index
          }
        });
        
        this.$vs.notification({
          title: "Job Removed",
          text: response.data.message || "Job removed successfully",
          color: "success",
        });
        
        // Refresh the queue jobs list immediately
        await this.viewQueueJobs(this.selectedQueueName);
        // Also refresh the main queue list to update job counts
        await this.getQueues();
        
      } catch (e) {
        console.error("Failed to remove job:", e);
        this.$vs.notification({
          title: "Job Removal Failed",
          text: e.response?.data?.message || "Failed to remove job",
          color: "danger",
        });
      } finally {
        // Reset loading state
        this.$set(job, 'isRemoving', false);
      }
    },

    viewJobDetails(job) {
      this.selectedJob = job;
      this.jobDetailsDialog = true;
    },

    toggleAutoRefresh() {
      if (this.autoRefresh) {
        this.refreshInterval = setInterval(() => {
          this.refreshAll();
        }, 10000); // 10 seconds
      } else {
        if (this.refreshInterval) {
          clearInterval(this.refreshInterval);
          this.refreshInterval = null;
        }
      }
    },

    async debugRedis() {
      try {
        const response = await axios.get("/redis-monitor/debug");
        console.log("Redis Debug Info:", response.data);
        
        this.$vs.notification({
          title: "Debug Info",
          text: `Found ${response.data.data.total_keys} total Redis keys. Check console for details.`,
          color: "primary",
        });
      } catch (e) {
        console.error("Failed to get debug info:", e);
        this.$vs.notification({
          title: "Debug Failed",
          text: "Could not retrieve Redis debug information",
          color: "danger",
        });
      }
    },

    async debugLogs() {
      try {
        const response = await axios.get("/redis-monitor/logs");
        console.log("Logs Response:", response.data);
        
        this.$vs.notification({
          title: "Logs Debug",
          text: `Found ${response.data.data.length} log entries. Log file: ${response.data.log_file_used || 'Unknown'}`,
          color: "primary",
        });
      } catch (e) {
        console.error("Failed to get logs debug info:", e);
        this.$vs.notification({
          title: "Logs Debug Failed",
          text: "Could not retrieve logs debug information",
          color: "danger",
        });
      }
    },

    formatUptime(seconds) {
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor((seconds % 86400) / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      
      if (days > 0) {
        return `${days}d ${hours}h`;
      } else if (hours > 0) {
        return `${hours}h ${minutes}m`;
      } else {
        return `${minutes}m`;
      }
    },

    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toString();
    },

    calculateHitRate(hits, misses) {
      const total = hits + misses;
      if (total === 0) return '0';
      return ((hits / total) * 100).toFixed(1);
    },

    formatTTL(ttl) {
      if (ttl === -1) return "No expiry";
      if (ttl === -2) return "Expired";
      return `${ttl}s`;
    },

    getLogLevelClass(level) {
      const levelMap = {
        ERROR: "log-error",
        WARNING: "log-warning",
        INFO: "log-info",
        DEBUG: "log-debug",
      };
      return levelMap[level] || "log-info";
    },

    getTypeColor(type) {
      const typeColors = {
        string: "blue",
        list: "green",
        set: "orange",
        hash: "purple",
        zset: "red",
      };
      return typeColors[type] || "grey";
    },

    // Clear log search filters
    clearLogSearch() {
      this.logSearchText = "";
      this.logLevelFilter = [];
    },

    // Highlight search text in log message
    highlightSearchText(text) {
      if (!this.logSearchText) {
        return text;
      }
      
      const searchText = this.logSearchText.trim();
      if (!searchText) {
        return text;
      }

      // Escape special regex characters
      const escapedSearch = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedSearch})`, 'gi');
      
      return text.replace(regex, '<mark class="log-highlight">$1</mark>');
    },
  },
};
</script>

<style scoped>
.log-container {
  max-height: 500px;
  overflow-y: auto;
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  font-family: monospace;
}

.log-entry {
  margin-bottom: 8px;
  padding: 4px 8px;
  border-radius: 3px;
  white-space: pre-wrap;
  word-break: break-all;
}

.log-timestamp {
  color: #666;
  margin-right: 8px;
}

.log-level {
  font-weight: bold;
  margin-right: 8px;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
}

.log-message {
  color: #333;
}

.log-error {
  background-color: #ffebee;
  border-left: 4px solid #f44336;
}

.log-error .log-level {
  background-color: #f44336;
  color: white;
}

.log-warning {
  background-color: #fff3e0;
  border-left: 4px solid #ff9800;
}

.log-warning .log-level {
  background-color: #ff9800;
  color: white;
}

.log-info {
  background-color: #e3f2fd;
  border-left: 4px solid #2196f3;
}

.log-info .log-level {
  background-color: #2196f3;
  color: white;
}

.log-debug {
  background-color: #f3e5f5;
  border-left: 4px solid #9c27b0;
}

.log-debug .log-level {
  background-color: #9c27b0;
  color: white;
}

.error-text {
  background: #ffebee;
  padding: 16px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 300px;
  overflow-y: auto;
}

pre {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 300px;
  overflow-y: auto;
}

/* Clean Header Bar */
.header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: white;
  border-bottom: 1px solid #eee;
  border-radius: 4px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #dc3545;
  transition: background 0.3s ease;
}

.status-indicator.active {
  background: #28a745;
}

.header-center {
  display: flex;
  gap: 24px;
  flex: 1;
  justify-content: center;
}

.metric {
  text-align: center;
}

.metric .value {
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
}

.metric .label {
  display: block;
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.auto-refresh-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.auto-refresh-toggle input {
  width: 14px;
  height: 14px;
}

.toggle-text {
  font-size: 13px;
  color: #666;
}

.refresh-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover {
  background: #e9ecef;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .header-bar {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }
  
  .header-center {
    gap: 16px;
    justify-content: space-around;
    width: 100%;
  }
  
  .metric .value {
    font-size: 14px;
  }
}

/* Log search highlight */
.log-highlight {
  background-color: #ffeb3b;
  color: #000;
  padding: 1px 2px;
  border-radius: 2px;
  font-weight: bold;
}

/* Improve log message readability with search highlighting */
.log-message {
  line-height: 1.4;
}
</style>